import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Sparkles, Search, LogOut, Menu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { rolLabel } from '../utils/helpers';
import NotificationCenter from './NotificationCenter';
import ThemeToggle from './ThemeToggle';

// Disparar la apertura del CommandPalette desde el topbar.
// Sintetiza el atajo Ctrl+K como evento global.
const openCommandPalette = () => {
    window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }));
};

// Detectar si el SO es Mac para mostrar el shortcut correcto.
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent || '');

/**
 * Topbar — header superior sticky.
 *
 * Props:
 *   - onMenuToggle: callback para abrir/cerrar el sidebar en mobile/tablet
 *   - showMenuButton: muestra el icono hamburguesa (solo en mobile/tablet)
 */
const Topbar = ({ onMenuToggle, showMenuButton = false, sidebarOffset = 0 }) => {
    const { user, tienePermiso, logout } = useAuth();
    const navigate = useNavigate();
    const puedeUsarIa = tienePermiso('usar_ia');

    // Scroll-aware: oculta al bajar, muestra al subir o cerca del top.
    const [visible, setVisible] = useState(true);
    const lastY = useRef(0);
    const ticking = useRef(false);

    useEffect(() => {
        const HIDE_THRESHOLD = 80;
        const DELTA = 6;

        // El scroll del proyecto puede estar en window, #root, o en el div padre
        // del Topbar (segun el layout). Detectamos cual scrollea y nos enganchamos a el.
        const candidates = [];
        candidates.push(window);
        const root = document.getElementById('root');
        if (root) candidates.push(root);
        // Padre del header (el div flex-1 con overflow-x-hidden de App.js).
        // Lo resolvemos por el target del primer scroll que recibamos.

        const getY = (target) => {
            if (target === window) return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
            return target.scrollTop || 0;
        };

        const handle = (target) => () => {
            if (ticking.current) return;
            ticking.current = true;
            window.requestAnimationFrame(() => {
                const y = getY(target);
                const diff = y - lastY.current;
                if (y < HIDE_THRESHOLD) {
                    setVisible(true);
                } else if (diff > DELTA) {
                    setVisible(false);
                } else if (diff < -DELTA) {
                    setVisible(true);
                }
                lastY.current = y;
                ticking.current = false;
            });
        };

        const listeners = candidates.map(t => {
            const fn = handle(t);
            t.addEventListener('scroll', fn, { passive: true });
            return [t, fn];
        });

        return () => listeners.forEach(([t, fn]) => t.removeEventListener('scroll', fn));
    }, []);

    return (
        <>
        {/* Spacer: ocupa el alto del topbar fixed para que el contenido no quede tapado */}
        <div className="h-[76px] shrink-0" aria-hidden="true" />
        <header
            style={{ left: sidebarOffset, right: 0 }}
            className={`
                fixed top-0 z-40
                flex items-center justify-between gap-2 sm:gap-3
                min-h-[60px] px-3 sm:px-6 py-3
                bg-surface/85 backdrop-blur-md
                border-b border-line
                transition-transform duration-300 ease-out will-change-transform
                ${visible ? 'translate-y-0' : '-translate-y-[110%]'}
            `}
        >
            {/* Slot izquierdo: hamburguesa (mobile) + buscador global */}
            <div className="flex items-center gap-2 flex-1 min-w-0 max-w-md">
                {showMenuButton && (
                    <button
                        type="button"
                        onClick={onMenuToggle}
                        className="
                            inline-flex items-center justify-center
                            w-10 h-10 rounded-lg shrink-0
                            bg-surface-2 hover:bg-line/60
                            border border-line
                            text-text
                            transition
                        "
                        aria-label="Abrir menú"
                    >
                        <Menu size={18} />
                    </button>
                )}
                <button
                    type="button"
                    onClick={openCommandPalette}
                    className="
                        inline-flex items-center gap-2.5
                        h-10 rounded-lg shrink-0 sm:flex-1
                        w-10 sm:w-auto px-0 sm:px-3
                        justify-center sm:justify-start
                        bg-surface-2 hover:bg-line/60
                        border border-line
                        text-sm text-muted
                        transition group
                    "
                    aria-label="Buscar"
                >
                    <Search size={15} />
                    <span className="hidden sm:flex-1 sm:inline text-left">Buscar...</span>
                    <span
                        className="
                            hidden md:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md
                            text-[10px] font-mono font-medium text-muted
                            bg-surface border border-line
                            group-hover:text-text
                        "
                    >
                        {isMac ? '⌘' : 'Ctrl'}+K
                    </span>
                </button>
            </div>

            {/* Slot derecho */}
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                {puedeUsarIa && (
                    <button
                        type="button"
                        onClick={() => navigate('/cotizaciones/generar-ia')}
                        aria-label="Cotizar con IA"
                        className="
                            ai-flash-btn
                            relative overflow-hidden isolate
                            inline-flex items-center justify-center gap-2
                            h-10 rounded-full shrink-0
                            w-10 sm:w-auto sm:px-4
                            text-sm font-medium text-text
                            bg-gradient-to-r from-purple2/20 to-cyan2/20
                            border border-purple2/40
                            hover:from-purple2/30 hover:to-cyan2/30 hover:border-purple2/60
                            transition
                        "
                    >
                        <Sparkles size={16} className="ai-flash-icon text-purple2 relative z-10" />
                        <span className="hidden sm:inline relative z-10 whitespace-nowrap">Cotizar con IA</span>
                    </button>
                )}

                <ThemeToggle />
                <NotificationCenter />

                {/* Perfil del usuario — compacto en mobile (solo avatar) */}
                <button
                    type="button"
                    onClick={() => navigate('/perfil')}
                    aria-label="Mi perfil"
                    className="
                        inline-flex items-center gap-2.5 shrink-0
                        h-10 rounded-full
                        w-10 md:w-auto md:pl-1.5 md:pr-4
                        justify-center md:justify-start
                        md:max-w-[220px]
                        bg-surface-2 hover:bg-line/60
                        border border-line
                        transition
                    "
                >
                    <span
                        className="
                            inline-flex items-center justify-center
                            w-7 h-7 rounded-full
                            bg-primary/15 text-primary
                            shrink-0
                        "
                    >
                        <User size={14} />
                    </span>
                    <span className="hidden md:flex flex-col items-start min-w-0 leading-[1.15]">
                        <span className="text-sm font-medium text-text truncate max-w-full">
                            {user?.nombre || 'Usuario'}
                        </span>
                        {user?.rol && (
                            <span className="text-[9px] uppercase tracking-[0.5px] text-muted/80 truncate max-w-full">
                                {rolLabel(user.rol)}
                            </span>
                        )}
                    </span>
                </button>

                {/* Cerrar sesion — siempre tinta roja para dar peso */}
                <button
                    type="button"
                    onClick={logout}
                    aria-label="Cerrar sesión"
                    className="
                        inline-flex items-center justify-center shrink-0
                        w-10 h-10 rounded-full
                        bg-danger/10 hover:bg-danger/20
                        border border-danger/40 hover:border-danger
                        text-danger
                        transition
                        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/40
                    "
                >
                    <LogOut size={17} strokeWidth={2.25} />
                </button>
            </div>
        </header>
        </>
    );
};

export default Topbar;
